import { type RefObject, useLayoutEffect, useRef } from 'react';
import {
  ConversationPanelRoot,
  ConversationPaneSlot,
} from '@/components/conversation-panel-layout';
import type { ConversationLayoutNode } from '@/lib/conversations/conversation-layout';
import { panelSplitTabs } from '@/lib/desktop-shell/panel-split';

/** Where a tab of the split shows, in the panel's content box. */
export type PanelSplitRect = { left: number; top: number; width: number; height: number };

/**
 * The panel split between tabs: resizable cells, named by their tabs only. The tabs' views are
 * not rendered in the cells (moving them would remount them, and a terminal would end): the panel
 * lays each one over its cell's box, which a cell reports as it changes.
 */
export function PanelSplitFrame({
  containerRef,
  onRect,
  split,
}: {
  containerRef: RefObject<HTMLElement | null>;
  onRect: (tabId: string, rect: PanelSplitRect) => void;
  split: ConversationLayoutNode;
}) {
  return (
    <div className="absolute inset-0 flex">
      <ConversationPanelRoot node={split}>
        {panelSplitTabs(split).map((tabId) => (
          <ConversationPaneSlot key={tabId} paneId={tabId}>
            <PanelSplitCell containerRef={containerRef} onRect={onRect} tabId={tabId} />
          </ConversationPaneSlot>
        ))}
      </ConversationPanelRoot>
    </div>
  );
}

function PanelSplitCell({
  containerRef,
  onRect,
  tabId,
}: {
  containerRef: RefObject<HTMLElement | null>;
  onRect: (tabId: string, rect: PanelSplitRect) => void;
  tabId: string;
}) {
  const cellRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const cell = cellRef.current;
    const container = containerRef.current;
    if (cell === null || container === null) return;
    const measure = () => {
      const box = cell.getBoundingClientRect();
      const content = container.getBoundingClientRect();
      onRect(tabId, {
        left: box.left - content.left,
        top: box.top - content.top,
        width: box.width,
        height: box.height,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cell);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, onRect, tabId]);
  return <div className="h-full min-h-0 min-w-0" data-panel-split-target={tabId} ref={cellRef} />;
}
